/**
 * 🚧 GTM EVENTS STAGING - Migration in Progress
 *
 * This file contains new GTM event implementations that are being validated
 * before connecting to production GTM.
 *
 * WORKFLOW:
 * 1. Implement event here (logs to console with feature flag)
 * 2. Validate with tracking analyst (100% complete including edge cases)
 * 3. Move implementation to events.ts (connects to real GTM)
 * 4. Update import in eventTrackers.ts
 *
 * WHY: We implement against console.log first to ensure we never ship partial
 * or inconsistent tracking to production. Events only connect to GTM when 100% ready.
 *
 * FEATURE FLAG: GTM_EVENTS_MIGRATION_LOGS_ACTIVATED
 * Set in runtime config to enable console logging in non-production environments.
 *
 * @see https://backmarket.atlassian.net/wiki/spaces/BOT/pages/6197674093/Pillar+1+Upgrade+our+GTM+implementation
 */

import { useRuntimeConfig } from '#imports'
import type { RouteLocationNormalized } from 'vue-router'

import type { DataLayerEvent } from '../types'

/**
 * Logs event to console instead of pushing to GTM dataLayer.
 * Only logs when GTM_EVENTS_MIGRATION_LOGS_ACTIVATED feature flag is enabled.
 *
 * @param event - The event data that would be pushed to dataLayer
 */
function logEventToConsole(event: DataLayerEvent): void {
  if (!import.meta.client) return

  const {
    public: { GTM_EVENTS_MIGRATION_LOGS_ACTIVATED },
  } = useRuntimeConfig()

  if (!GTM_EVENTS_MIGRATION_LOGS_ACTIVATED) return

  // eslint-disable-next-line no-console
  console.log('[GTM Staging]', event)
}

// ============================================================================
// 🚧 STAGING EVENTS
// Add new event implementations below.
// Use logEventToConsole() instead of pushEventToDataLayer() during migration.
// ============================================================================

export function trackPageView(to: RouteLocationNormalized) {
  logEventToConsole({
    event: 'page_view',
    page_referrer: document.referrer,
    page_category: to.name,
  })
}
